import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Mail, Bell, Clock, Activity, Plus, X, ArrowLeft, Loader2, Save, Play,
} from 'lucide-react';
import { toast } from 'sonner';

const PRESETS = [
  { label: 'Gentle (7 / 14 / 30)', values: [7, 14, 30] },
  { label: 'Default (3 / 7 / 14)', values: [3, 7, 14] },
  { label: 'Aggressive (1 / 3 / 7)', values: [1, 3, 7] },
];

const AlertPreferences = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [recipient, setRecipient] = useState('');
  const [threshold, setThreshold] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/billing/alert-preferences');
      setPrefs(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to load preferences');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => setPrefs((p) => ({ ...p, ...patch }));

  const save = async (overrides = {}) => {
    const payload = {
      recipients: (overrides.recipients ?? prefs.recipients),
      overdue_thresholds: overrides.overdue_thresholds ?? prefs.overdue_thresholds,
      digest_enabled: overrides.digest_enabled ?? prefs.digest_enabled,
      realtime_enabled: overrides.realtime_enabled ?? prefs.realtime_enabled,
      stale_days: overrides.stale_days ?? prefs.stale_days,
    };
    setSaving(true);
    try {
      const { data } = await api.put('/admin/billing/alert-preferences', payload);
      setPrefs(data);
      toast.success('Preferences saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = () => {
    const clean = recipient.trim().toLowerCase();
    if (!clean || !/^\S+@\S+\.\S+$/.test(clean)) {
      toast.error('Enter a valid email'); return;
    }
    if ((prefs.recipients || []).includes(clean)) {
      toast.error('Already in list'); return;
    }
    const next = [...(prefs.recipients || []), clean];
    update({ recipients: next });
    setRecipient('');
  };

  const removeRecipient = (email) => {
    const next = prefs.recipients.filter((e) => e !== email);
    if (!next.length) { toast.error('At least one recipient required'); return; }
    update({ recipients: next });
  };

  const addThreshold = () => {
    const n = parseInt(threshold, 10);
    if (!n || n < 1 || n > 365) { toast.error('Threshold must be 1–365 days'); return; }
    if ((prefs.overdue_thresholds || []).includes(n)) { toast.error('Already in list'); return; }
    const next = [...(prefs.overdue_thresholds || []), n].sort((a, b) => a - b);
    update({ overdue_thresholds: next });
    setThreshold('');
  };

  const removeThreshold = (v) => {
    const next = (prefs.overdue_thresholds || []).filter((t) => t !== v);
    if (!next.length) { toast.error('At least one threshold required'); return; }
    update({ overdue_thresholds: next });
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/admin/billing/run-alerts');
      const per = data?.result?.per_invoice;
      const dig = data?.result?.digest;
      const sent = per?.sent ?? 0;
      if (per?.skipped) {
        toast.info(`Real-time alerts: ${per.skipped}`);
      } else {
        toast.success(`Scanned ${per?.scanned ?? 0} overdue · ${sent} alert email(s) sent`);
      }
      if (dig?.skipped) toast.info(`Digest: ${dig.skipped}`);
      else if (dig?.sent) toast.success(`Daily digest sent (${dig.count} invoice${dig.count === 1 ? '' : 's'})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to run alerts');
    } finally {
      setRunning(false);
    }
  };

  if (loading || !prefs) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6" data-testid="alert-preferences-page">
      <div className="max-w-3xl mx-auto px-4">
        <button
          onClick={() => navigate('/admin/home')}
          className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 mb-4"
          data-testid="alert-prefs-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Billing Alert Preferences</h1>
            <p className="text-sm text-gray-500 mt-0.5">Control who hears about billing events and when.</p>
          </div>
          <Button
            variant="outline"
            onClick={runNow}
            disabled={running}
            data-testid="alert-prefs-run-now"
          >
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            Run alert scan now
          </Button>
        </div>

        {/* Recipients */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="w-4 h-4 text-cyan-600" /> Alert recipients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-3">
              {(prefs.recipients || []).map((e) => (
                <span key={e} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-cyan-50 border border-cyan-200 text-xs text-cyan-900" data-testid={`recipient-chip-${e}`}>
                  {e}
                  <button
                    onClick={() => removeRecipient(e)}
                    className="text-cyan-700 hover:text-red-600"
                    aria-label={`Remove ${e}`}
                    data-testid={`remove-recipient-${e}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="finance@ievntur.com"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
                data-testid="recipient-input"
              />
              <Button onClick={addRecipient} variant="outline" data-testid="add-recipient-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
              These emails receive every billing alert (invoice paid, overdue escalations, daily digest, net-terms requests).
            </p>
          </CardContent>
        </Card>

        {/* Channels */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Bell className="w-4 h-4 text-amber-600" /> Channels
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Real-time per-invoice escalations</Label>
                <p className="text-xs text-gray-500">One email the first day an invoice hits each overdue threshold.</p>
              </div>
              <Switch
                checked={prefs.realtime_enabled}
                onCheckedChange={(v) => update({ realtime_enabled: v })}
                data-testid="toggle-realtime"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Daily overdue digest</Label>
                <p className="text-xs text-gray-500">Single summary email at 10:15 UTC listing every open overdue invoice.</p>
              </div>
              <Switch
                checked={prefs.digest_enabled}
                onCheckedChange={(v) => update({ digest_enabled: v })}
                data-testid="toggle-digest"
              />
            </div>
            {!prefs.realtime_enabled && !prefs.digest_enabled && (
              <p className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">
                Heads up: both channels off means no overdue alerts will go out.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Thresholds */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="w-4 h-4 text-red-600" /> Overdue escalation thresholds
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-3">
              {(prefs.overdue_thresholds || []).map((d) => (
                <span key={d} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-50 border border-red-200 text-xs text-red-900 font-semibold" data-testid={`threshold-chip-${d}`}>
                  {d} day{d === 1 ? '' : 's'}
                  <button
                    onClick={() => removeThreshold(d)}
                    className="text-red-700 hover:text-red-900"
                    aria-label={`Remove ${d}-day threshold`}
                    data-testid={`remove-threshold-${d}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mb-3">
              <Input
                type="number"
                min="1"
                max="365"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                placeholder="Days overdue (e.g. 30)"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addThreshold(); } }}
                data-testid="threshold-input"
              />
              <Button onClick={addThreshold} variant="outline" data-testid="add-threshold-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <Button
                  key={p.label}
                  size="sm"
                  variant="outline"
                  onClick={() => update({ overdue_thresholds: [...p.values] })}
                  data-testid={`preset-${p.values.join('-')}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <p className="text-[11px] text-gray-500 mt-3">
              An "immediate" alert always fires the first day an invoice goes overdue. Additional thresholds above
              escalate from there — we only send the highest-severity email per invoice per run (no spam).
            </p>
          </CardContent>
        </Card>

        {/* Stale */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="w-4 h-4 text-yellow-600" /> Stale invoice threshold
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min="1"
                max="90"
                value={prefs.stale_days}
                onChange={(e) => update({ stale_days: parseInt(e.target.value || '7', 10) })}
                className="max-w-[120px]"
                data-testid="stale-days-input"
              />
              <span className="text-sm text-gray-600">days of inactivity → flagged as stale</span>
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
              An invoice is marked stale when it's overdue and the last reminder activity was more than
              this many days ago. Stale invoices appear on the admin home as a "leaking revenue" warning.
            </p>
          </CardContent>
        </Card>

        <div className="sticky bottom-4 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={load}
            disabled={saving}
            data-testid="alert-prefs-reset"
          >
            Discard
          </Button>
          <Button
            onClick={() => save()}
            disabled={saving}
            className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-lg"
            data-testid="alert-prefs-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save preferences
          </Button>
        </div>

        {prefs.updated_at && (
          <p className="text-[11px] text-gray-400 text-right mt-4">
            Last updated {new Date(prefs.updated_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
};

export default AlertPreferences;
